import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { ServiceConditionForm } from "../ServiceConditionForm/ServiceConditionForm";
import { useServiceConditionMutations } from "../api";
import { emptyServiceConditionForm } from "../types";
import type { ServiceConditionFormValues } from "../types";

const FORM_ID = "service-condition-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useServiceConditionMutations();
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: ServiceConditionFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create service condition", err);
      setToast({ variant: "danger", text: "Could not save." });
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  return (
    <>
      <AdminFormPage
        title="Add a Term or Condition"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <ServiceConditionForm
          key={formKey}
          id={FORM_ID}
          initialValues={emptyServiceConditionForm}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          onInvalid={() =>
            setToast({
              variant: "danger",
              text: "Name and content must each be at least 3 characters.",
            })
          }
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
